import { useVistassistantMail } from "@/contexts/VistassistantMailContext";
import Email, { MailsListId } from "@/domain/entities/emails/Email";
import MailSource from "@/domain/enums/MailSource";
import { cn } from "@/lib/utils";
import { setOpenedMail } from "@/stores/mailStore";
import { Dispatch, forwardRef, SetStateAction, useMemo, useState } from "react";
import SmartLabelIcon from "../SmartLabelIcon";
import StopPropagateDiv from "../StopPropagateDiv";
import { Card } from "../ui/card";
import { Checkbox } from "../ui/checkbox";
import CategoriesList from "./CategoriesList";
import GroupAvatars from "./GroupAvatars";
import { MailAction } from "./MailAction";
import MailAISummary from "./MailAISummary";
import MailDate from "./MailDate";
import MailIcons from "./MailIcons";
import MailTitle from "./MailTitle";
import TreatedTag from "./TreatedTag";

type MailRowProps = {
    mail: Email;
    onSelectChange?: (checked: boolean, mail: Email) => void;
    squeeze: boolean;
    isOpened: boolean;
    listId: MailsListId;
    setMailInputExpanded: (mailInputExpanded: boolean) => void;
    setSelectedMails: Dispatch<SetStateAction<Email[]>>;
    selectedMails: Email[];
};

const MailRow = forwardRef<HTMLDivElement, MailRowProps>(
    (
        {
            mail,
            onSelectChange,
            squeeze,
            isOpened,
            listId,
            setMailInputExpanded,
            setSelectedMails,
            selectedMails,
        },
        ref,
    ) => {
        const { isVistassistantResult, onMailClick } = useVistassistantMail();
        const [keepActionsVisible, setKeepActionsVisible] = useState(false);

        const variant = mail.getMailStatus();
        const { isRead } = mail;
        const isSentList = listId === MailSource.SentItems;

        const checked = useMemo(
            () => selectedMails.includes(mail),
            [selectedMails, mail],
        );

        return (
            <Card
                ref={ref}
                className={cn(
                    "group relative max-h-23 overflow-hidden",
                    checked && "border-primary-darker",
                    squeeze
                        ? "max-h-31"
                        : "flex items-center gap-2 [container-name:mailrow] [container-type:inline-size]",
                )}
                data-mail-id={mail.id}
                display={squeeze ? "grid" : "default"}
                forceHover={keepActionsVisible}
                onClick={() => {
                    if (isVistassistantResult && onMailClick) {
                        onMailClick(mail, listId);
                    } else if (!isOpened) {
                        setOpenedMail(mail);
                        setMailInputExpanded(false);
                    }
                }}
                opened={isOpened}
                size={squeeze ? "1x4" : "default"}
                variant={variant}
            >
                <div
                    className={cn(
                        "flex justify-start gap-2",
                        squeeze
                            ? "row-span-3 flex-col"
                            : "w-70 items-center overflow-hidden pr-2",
                    )}
                >
                    <div className="flex items-center gap-2">
                        <div className="flex flex-col items-center justify-center gap-2">
                            {!squeeze &&
                                mail.aiDetails &&
                                mail.smartLabel &&
                                !isSentList && (
                                    <SmartLabelIcon
                                        smartLabel={mail.smartLabel}
                                    />
                                )}
                            <StopPropagateDiv className="h-4">
                                <Checkbox
                                    checked={checked}
                                    className="h-4 w-4"
                                    onCheckedChange={(isChecked: boolean) =>
                                        onSelectChange &&
                                        onSelectChange(Boolean(isChecked), mail)
                                    }
                                />
                            </StopPropagateDiv>
                        </div>
                        {squeeze && (
                            <MailDate sentDateTime={mail.sentDateTime} />
                        )}
                    </div>
                    <div className="flex w-full min-w-0 flex-col">
                        <GroupAvatars mail={mail} />
                        <div
                            className={cn(
                                "flex items-center gap-2",
                                !squeeze && "w-0 min-w-full",
                            )}
                        >
                            {squeeze &&
                                mail.aiDetails &&
                                mail.smartLabel &&
                                !isSentList && (
                                    <SmartLabelIcon
                                        smartLabel={mail.smartLabel}
                                    />
                                )}
                            <MailTitle isRead={isRead} mail={mail} />
                        </div>
                    </div>
                </div>
                {!squeeze && (
                    <MailAISummary
                        className="ml-3 min-w-0 flex-1 items-center overflow-hidden"
                        isSentFolder={isSentList}
                        mail={mail}
                        variant="long"
                    />
                )}
                <div
                    className={cn(
                        "relative flex gap-1",
                        squeeze
                            ? "row-start-4 items-center justify-between"
                            : "shrink-0 flex-col items-end w-67 overflow-hidden [@container_mailrow_(max-width:560px)]:w-auto",
                    )}
                >
                    {!squeeze && <MailDate sentDateTime={mail.sentDateTime} />}
                    <div className="flex items-center justify-end gap-1.5">
                        <MailIcons mail={mail} />
                        {!isSentList &&
                            (mail.isUserProcessed ? (
                                <div className="[@container_mailrow_(max-width:340px)]:hidden">
                                    <TreatedTag />
                                </div>
                            ) : (
                                <CategoriesList
                                    className="[@container_mailrow_(max-width:340px)]:hidden"
                                    mail={mail}
                                />
                            ))}
                    </div>
                </div>
                {selectedMails.length === 0 && (
                    <div
                        className={cn(
                            "absolute",
                            keepActionsVisible
                                ? "flex"
                                : "hidden group-hover:flex",
                            squeeze
                                ? "right-3 top-3"
                                : "bottom-0 right-35 top-0",
                        )}
                    >
                        <MailAction
                            className="my-auto"
                            listId={listId}
                            mail={mail}
                            onTooltipOpenChange={setKeepActionsVisible}
                            setMailInputExpanded={setMailInputExpanded}
                            setSelectedMails={setSelectedMails}
                        />
                    </div>
                )}
            </Card>
        );
    },
);

export default MailRow;
